import { isSSR } from 'Utilities/helpers';
import { safeLocalStorage } from 'Utilities/storages';

import { APIResponseStatus } from 'Services/api.types';

import { UploadDocType } from './uploadDocuments/UploadDocumentsPage.types';
import { DocumentKindsValue } from './DocumentCapture.types';

export const CONFIRM_SALE_ALLOWED_STATUSES = {
  SOLD: 'sold',
  SOLD_AWAITING: 'sold_wait',
  SOLD_CONFIRMED: 'sold_confirmed',
  SOLD_DOCUMENTS_REVIEWED: 'sold_documents_reviewed',
  SOLD_INVOICE_PAID: 'sold_invoice_paid',
  SOLD_INVOICE_RAISED: 'sold_invoice_raised',
  SOLD_PURCHASE_CONFIRMED: 'sold_purchase_confirmed',
} as const;

export const ACCEPT_OFFER_ALLOWED_STATUSES = {
  UNDER_OFFER: 'under_offer',
} as const;

export const DOCUMENTS_COMPLETED_ALLOWED_STATUSES = {
  SOLD_DOCUMENTS_REVIEWED: 'sold_documents_reviewed',
  SOLD_INVOICE_PAID: 'sold_invoice_paid',
  SOLD_INVOICE_RAISED: 'sold_invoice_raised',
  SOLD_PURCHASE_CONFIRMED: 'sold_purchase_confirmed',
} as const;

export const VIEW_TIMELINE_STATUSES = {
  CAR_GONE: 'car_gone',
  CUSTOMER_KEEP_CAR: 'customer_keep_car',
  CUSTOMER_LOST: 'customer_lost',
  FOR_SALE: 'for_sale',
  FULL_DETAILS: 'full_details',
  FULL_DETAILS_CONTACTED: 'full_details_contacted',
  FULL_DETAILS_WAIT: 'full_details_wait',
  QA_FAILED: 'qa_failed',
  QA_ON_HOLD: 'qa_on_hold',
  QA_READY: 'qa_ready',
  READY_FOR_SALE: 'ready_for_sale',
  SALE_ACTIVE: 'sale_active',
  TOO_EXPENSIVE: 'too_expensive',
  UNDER_OFFER: 'under_offer',
  UNSELLABLE: 'unsellable',
  UNSOLD_BELOW_RESERVE_BID: 'unsold_below_reserve_bid',
  UNSOLD_NO_BIDS: 'unsold_no_bids',
  WRITE_OFF: 'write_off',
} as const;

export const DOCUMENTS_PROMPT_STATUSES = [
  VIEW_TIMELINE_STATUSES.READY_FOR_SALE,
  VIEW_TIMELINE_STATUSES.QA_ON_HOLD,
  VIEW_TIMELINE_STATUSES.FOR_SALE,
  VIEW_TIMELINE_STATUSES.SALE_ACTIVE,
];

export const DOCS_EARLY_ALLOWED_STATUSES = [
  ...DOCUMENTS_PROMPT_STATUSES,
  VIEW_TIMELINE_STATUSES.QA_READY,
] as const;

export const SALE_CONFIRMED_STATUSES = [
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_CONFIRMED,
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_PURCHASE_CONFIRMED,
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_DOCUMENTS_REVIEWED,
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_INVOICE_RAISED,
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_INVOICE_PAID,
] as const;

export const DOCUMENT_CAPTURE_REVIEW_COMPLETE_ALLOWED_STATES = [
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_DOCUMENTS_REVIEWED,
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_INVOICE_RAISED,
  CONFIRM_SALE_ALLOWED_STATUSES.SOLD_INVOICE_PAID,
] as const;

export const DOCUMENT_CAPTURE_ROUTE_NAMES = {
  ADD_BANK_DETAILS: 'add-bank-details',
  AUTOMATED_DOCS_PAGE: 'automated-docs-upload',
  CONFIRM_SALE: 'confirm-sale',
  CONFIRM_SALE_SUMMARY: 'confirm-sale-summary',
  DEALER_DETAILS: 'purchasing-dealer-details',
  REVIEW_STATUS: 'review-status',
  UPLOAD_ADDITIONAL_DOCUMENTS: 'additional_documents',
  UPLOAD_BANK_STATEMENT: 'bank-statement',
  UPLOAD_DEATH_CERTIFICATE: 'death-certificate',
  UPLOAD_DOCUMENTS: 'upload-documents',
  UPLOAD_DRIVING_LICENCE: 'driving-licence',
  UPLOAD_FINANCE_SETTLEMENT_LETTER: 'finance-letter',
  UPLOAD_FINISH: 'finish',
  UPLOAD_LETTER_OF_CONSENT: 'letter-of-consent',
  UPLOAD_PASSPORT: 'passport',
  UPLOAD_PROOF_OF_ADDRESS: 'proof-of-address',
  UPLOAD_PROOF_OF_PURCHASE: 'proof-of-purchase',
  UPLOAD_SOLICITORS_LETTER: 'solicitors-letter',
  UPLOAD_V5: 'v5c',
  UPLOAD_WILL: 'will',
  VEHICLE_COLLECTION: 'collection',
} as const;

export const DOCUMENT_CAPTURE_ROUTE_NAMES_ARRAY = Object.values(DOCUMENT_CAPTURE_ROUTE_NAMES);

export const DOCUMENT_CAPTURE_RESPONSE_API_KEYS = {
  BANK_DETAILS_ADDED: 'bankDetailsAdded',
  BANK_DETAILS_REVIEW_FAILED: 'bankDetailsReviewFailed',
  CONFIRMED_SALE: 'confirmedBySeller',
  DOCUMENTS: 'documents',
  DOCUMENTS_FULFILLED: 'documentsFulfilled',
  DOCUMENTS_IMAGE_ACTION_REUPLOAD: 'needsReupload',
  DOCUMENTS_REQUIRED: 'required',
  DOCUMENTS_SUBMITTED: 'submitted',
  DOCUMENTS_UPLOADED: 'uploaded',
} as const;

export const PAYMENT_STATUS_ROUTE_NAMES = {
  FINANCE_FUNDS_RECEIVED: 'finance-funds-received',
  SALE_COMPLETE: 'sale-complete',
  VEHICLE_PAYMENT: 'vehicle-payment',
} as const;

export const DOCUMENT_KINDS = {
  ADDITIONAL_DOCUMENTS: 'additional_documents',
  BANK_STATEMENT: 'bank_statement',
  DEATH_CERTIFICATE: 'death_certificate',
  DRIVING_LICENCE: 'driving_licence',
  FINANCE_SETTLEMENT_LETTER: 'finance_settlement_letter',
  LETTER_OF_CONSENT: 'letter_of_consent',
  PASSPORT: 'passport',
  PROOF_OF_PURCHASE: 'proof_of_purchase',
  SOLICITORS_LETTER: 'solicitors_letter',
  UTILITY_BILL: 'utility_bill',
  V5: 'v5',
  WILL: 'will',
} as const;

export const DOCUMENT_KIND_FINANCIAL_LETTER = 'finance_letter';
export const DOCUMENT_KIND_PROOF_OF_ADDRESS = 'proof_of_address';

export enum DOCUMENT_UPLOAD_TYPE {
  DEFAULT = 'default',
  EARLY = 'early',
  STANDALONE = 'standalone',
}
export const DOCUMENT_KINDS_VALUES = Object.values(DOCUMENT_KINDS);

export const DOCUMENT_ORDER = [
  DOCUMENT_KINDS.V5,
  DOCUMENT_KINDS.DRIVING_LICENCE,
  DOCUMENT_KINDS.PROOF_OF_PURCHASE,
  DOCUMENT_KINDS.FINANCE_SETTLEMENT_LETTER,
  DOCUMENT_KINDS.BANK_STATEMENT,
  DOCUMENT_KINDS.DEATH_CERTIFICATE,
  DOCUMENT_KINDS.UTILITY_BILL,
  DOCUMENT_KINDS.LETTER_OF_CONSENT,
  DOCUMENT_KINDS.WILL,
  DOCUMENT_KINDS.PASSPORT,
  DOCUMENT_KINDS.SOLICITORS_LETTER,
  DOCUMENT_KINDS.ADDITIONAL_DOCUMENTS,
] as const;

export const getUnifiedStatus = (statuses: string[] = []) => {
  if (statuses.some((status) => status === APIResponseStatus.ERROR)) {
    return APIResponseStatus.ERROR;
  }

  if (statuses.some((status) => status === APIResponseStatus.LOADING)) {
    return APIResponseStatus.LOADING;
  }

  if (statuses.every((status) => status === APIResponseStatus.SUCCESS)) {
    return APIResponseStatus.SUCCESS;
  }

  return APIResponseStatus.ERROR;
};

type Action = {
  value: boolean;
}

export type Actions = {
  [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS_IMAGE_ACTION_REUPLOAD]?: Action;
}

export type Image = {
  actions: Actions;
  documentId?: number;
  file?: File;
  imageUrl?: React.Key | null;
  isSharp?: boolean;
  kind?: string;
  pageNumber: number;
  sharpness?: number;
  url?: string;
}

export type DocumentUploadSource = 'webFrame' | 'uploaded';

export type DocumentTypes = typeof DOCUMENT_KINDS_VALUES[number];

export type Document = {
  hasAddressMismatch?: boolean;
  images?: Image[];
  kind?: DocumentKindsValue;
  required: boolean;
  review?: string;
  submitted?: boolean;
  uploaded?: boolean;
}
// Partial is because this is an API call
// Omit is because the API has documents as a key and in the webapp we use 'images' as the key for the same thing
export type DocumentStateAPI = Partial<Omit<Document, 'images'>> & { documents: Image[] };

export type State = {
  [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS]?: Document[];
  [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.BANK_DETAILS_ADDED]?: boolean;
  [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.BANK_DETAILS_REVIEW_FAILED]?: boolean;
  [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.CONFIRMED_SALE]?: boolean;
  [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS_FULFILLED]?: boolean;
}

export type SummaryTileReactivationProps = {
  node?: keyof State;
  state: State;
}

interface UploadAnalyticsEventString {
  hasRequestedReuploadActions?: boolean;
  message?: string;
  uploadDocType?: UploadDocType;
}

export const generateUploadDocumentAnalyticsEventString = ({
  hasRequestedReuploadActions, message = 'N/A', uploadDocType,
}: UploadAnalyticsEventString) => {
  let result = message;

  // If docs early and reentry, both will be appended to the SP event like *_early_reentry
  if (uploadDocType === DOCUMENT_UPLOAD_TYPE.EARLY) {
    result += '_early';
  }

  // If docs standalone and reentry, both will be appended to the SP event like *_standalone_reentry
  if (uploadDocType === DOCUMENT_UPLOAD_TYPE.STANDALONE) {
    result += '_standalone';
  }

  if (hasRequestedReuploadActions) {
    result += '_reentry';
  }

  return result;
};

export const checkDocumentActionStatus = (documentState?: Document[]) =>
  !!documentState?.some((doc: Document) => Object.values(doc?.images ?? {})
    ?.some((image: Image) => image.actions[DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS_IMAGE_ACTION_REUPLOAD]?.value));

export const resolveSummaryTileReactivation = ({ node, state }: SummaryTileReactivationProps) => {
  const stateNode = Boolean(node && state[node]);

  if (node === DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS && stateNode) {
    const hasImagesNeedingReupload = checkDocumentActionStatus(state[node] as Document[]);

    return hasImagesNeedingReupload;
  }

  if (node && stateNode) {
    return stateNode;
  }

  return false;
};

export const SaleConfirmationSentryOpts = {
  tags: {
    page: 'Sale confirmation',
  },
};

export const DOCUMENT_CAPTURE_REVIEW_KEY = 'reviewStatusView';
export const DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY = 'automatedReviewStatusView';

const getAutomatedReviewEnquiries = (): string[] => (isSSR()
  ? []
  : safeLocalStorage.getItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY) || []);

export const hasAutomatedReviewEnquiry = (
  enquiryId?: number | string | null,
) => Boolean(enquiryId) && getAutomatedReviewEnquiries().includes(String(enquiryId));

export const addAutomatedReviewEnquiry = (enquiryId?: number | string | null) => {
  if (!enquiryId || hasAutomatedReviewEnquiry(enquiryId)) {
    return;
  }

  safeLocalStorage.setItem(
    DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY,
    [...getAutomatedReviewEnquiries(), String(enquiryId)],
  );
};

export const FILES_UPLOAD_TYPES = 'image/jpeg,image/heic,image/png,image/bmp,image/heif,image/webp,image/tiff,.heic,.pdf';

export const LINKS_TO_DOCUMENT_CAPTURE_ID = 'links-to-document-capture';

export const PREVIEWABLE_FILE_TYPES = 'image/jpeg,image/heic,image/png,image/bmp,image/heif,image/webp,image/tiff,.heic';

export const DOCUMENT_UPLOAD_EXIT_PATHS = {
  [DOCUMENT_UPLOAD_TYPE.DEFAULT]: 'upload-documents',
  [DOCUMENT_UPLOAD_TYPE.EARLY]: `profile/timeline#${LINKS_TO_DOCUMENT_CAPTURE_ID}`,
  [DOCUMENT_UPLOAD_TYPE.STANDALONE]: 'request/bank-details',
} as const;

export const DOCUMENT_ERROR_TRIGGER = {
  ON_CHANGE: 'on_change',
  ON_SUBMISSION: 'on_submit',
} as const;

export const getKindFromUrl = (param: string) => {
  let docKind = param?.replaceAll('-', '_');

  if (param === DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_V5) {
    docKind = DOCUMENT_KINDS.V5;
  }

  return docKind;
};

export const isRequiredDocsUploaded = ({ documents }: { documents?: Document[] | null }) => {
  const uploadedRequiredDocs = documents?.filter((doc) => doc.required)?.some((doc) => doc.uploaded);
  return uploadedRequiredDocs ?? false;
};

type DocumentEligibilityFields = Pick<Document, 'kind' | 'required' | 'uploaded'>;

// POP verification requires a document to be manually reviewed, so it can't be
// completed through Incode's automated flow — it must be uploaded up front.
export const isPopEligibleForIncode = (
  { documents }: { documents?: Partial<DocumentEligibilityFields>[] | null },
) => {
  const requiredPopDocs = (documents ?? [])
    .filter((doc) => doc.kind === DOCUMENT_KINDS.PROOF_OF_PURCHASE && doc.required);

  return requiredPopDocs.every((doc) => doc.uploaded === true);
};

// POP is excluded here because its eligibility is evaluated separately via
// isPopEligibleForIncode, so an uploaded POP alone should not block Incode.
export const hasNonPopDocsUploaded = (
  { documents }: { documents?: Partial<DocumentEligibilityFields>[] | null },
) => {
  const hasUploadedNonPopDoc = (documents ?? [])
    .some((doc) => doc.kind !== DOCUMENT_KINDS.PROOF_OF_PURCHASE && !!doc.uploaded);

  return hasUploadedNonPopDoc;
};

export const INCODE_POLLING_STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  PENDING: 'pending',
} as const;

export const INCODE_POLLING_ERRORS = {
  FAILED: 'VERIFICATION_FAILED',
  TIMEOUT: 'POLLING_TIMEOUT',
} as const;
